import type { ReactNode } from 'react';
import { cn } from '@shared/utils';
import { ChevronRightIcon, SparkleIcon } from '@shared/ui';

/** AI 估算後要顯示的來源說明。null 表示這次的值不是 AI 帶入的。 */
export interface EstimateMeta {
  confidence: 'high' | 'medium' | 'low';
  /** 模型產生的自由文字。**一律當純文字渲染**，不可當 HTML 插入 */
  notes: string;
}

const CONFIDENCE_LABELS: Record<EstimateMeta['confidence'], string> = {
  high: '高把握',
  medium: '中等把握',
  low: '低把握',
};

export interface NutrientSectionProps {
  isOpen: boolean;
  onToggle: () => void;
  /** 有值時顯示來源橫幅與復原鍵 */
  estimate: EstimateMeta | null;
  onUndoEstimate: () => void;
  /** 五個欄位，由呼叫端以 form.Field 渲染 */
  children: ReactNode;
}

/**
 * 表單裡的營養素區塊：預設收合，AI 帶入後自動展開。
 *
 * 收合是為了保住這張表單最大的優點——兩個欄位就能送出。五個選填欄位攤開在那裡，
 * 會讓每次記錄都像要填一份問卷。
 *
 * AI 帶入時**必須展開**：收合著填等於背著使用者改東西，他之後發現數字不是自己打的，
 * 就再也不會信這顆按鈕。
 */
export function NutrientSection({
  isOpen,
  onToggle,
  estimate,
  onUndoEstimate,
  children,
}: NutrientSectionProps) {
  return (
    <section className="flex flex-col gap-2">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className={cn(
          'flex min-h-[44px] items-center gap-1.5 rounded-md text-left',
          'text-xs tracking-wide text-muted-foreground',
          'transition-colors duration-200 ease-out hover:text-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        )}
      >
        <ChevronRightIcon
          className={cn(
            'size-3.5 transition-transform duration-200 ease-out',
            isOpen && 'rotate-90',
          )}
        />
        <span>營養素</span>
        {/* 收合時要看得出「選填」，展開後這個提示就多餘了 */}
        {!isOpen && !estimate && (
          <span className="text-muted-foreground/70">（選填）</span>
        )}
        {/* 收合時仍要看得出有 AI 帶入的值，否則使用者不知道底下已經有東西 */}
        {!isOpen && estimate && (
          <span className="flex items-center gap-1 text-accent">
            <SparkleIcon className="size-3" />
            已估算
          </span>
        )}
      </button>

      {isOpen && (
        <div className="flex flex-col gap-3">
          {estimate && (
            <div
              className={cn(
                'flex flex-col gap-1.5 rounded-md border border-border p-3',
                'bg-accent/5 text-xs',
              )}
            >
              <div className="flex items-center gap-2">
                <SparkleIcon className="size-3.5 shrink-0 text-accent" />
                <span className="flex-1 text-muted-foreground">
                  AI 估算 · {CONFIDENCE_LABELS[estimate.confidence]}
                </span>
                {/*
                  一顆復原鍵，不是每欄一顆：一次帶入六個欄位，逐欄復原太瑣碎。
                  整批回到按下之前的狀態，行為才可預期。
                */}
                <button
                  type="button"
                  onClick={onUndoEstimate}
                  className={cn(
                    'min-h-[44px] px-1 text-accent underline underline-offset-2',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  )}
                >
                  復原
                </button>
              </div>
              {/*
                notes 是模型從使用者輸入生成的自由文字。以純文字渲染（React 預設即會
                跳脫），絕不可改成 dangerouslySetInnerHTML —— 那是注入唯一能落地的地方。
              */}
              <p className="leading-relaxed text-muted-foreground">
                {estimate.notes}
              </p>
            </div>
          )}
          {children}
        </div>
      )}
    </section>
  );
}
